import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTAButton from '../components/CTAButton';
import SecurityBadges from '../components/SecurityBadges';

const Email: React.FC = () => {
  const { formData, updateFormData, nextStep } = useFormContext();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<{
    email?: string;
  }>({});
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate email when it changes and has been touched
  useEffect(() => {
    if (touched) {
      validateForm(false);
    }
  }, [formData.email, touched]);

  const validateForm = (isSubmitting = true) => {
    const newErrors: {
      email?: string;
    } = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse. Bitte überprüfen Sie Ihre Eingabe';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched(true);

    if (validateForm()) {
      setIsSubmitting(true);

      // Send user data to Google Ads for Enhanced Conversions matching (GCLID/WBRAID ↔ email)
      // Use gtag if available, fall back to tac_gtag (created by Google Consent Mode before full gtag loads)
      const gtagFn = typeof window.gtag === 'function' ? window.gtag : typeof window.tac_gtag === 'function' ? window.tac_gtag : null;
      if (gtagFn) {
        gtagFn('set', 'user_data', {
          email: formData.email.toLowerCase().trim()
        });
      }

      try {
        await nextStep({
          email: formData.email
        });
        navigate('/loading');
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    // Navigate back based on the knownInfo
    switch (formData.knownInfo) {
      case 'address':
        navigate('/address-details');
        break;
      case 'birthdate':
        navigate('/birthdate');
        break;
      case 'both':
        navigate('/both-info');
        break;
      default:
        navigate('/known-info');
    }
  };

  // Handle input blur to mark as touched
  const handleBlur = () => {
    setTouched(true);
  };

  // Get input container class based on error state
  const getContainerClass = () => {
    const baseClass = "flex items-center bg-[#f9fafb] border border-[#e2e8f0] rounded-md overflow-hidden";

    if (errors.email && touched) {
      return `${baseClass} border-red-500 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500 focus-within:ring-opacity-20`;
    }

    return `${baseClass} focus-within:border-[#003a5d] focus-within:ring-2 focus-within:ring-[#003a5d] focus-within:ring-opacity-20`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        <div className="form-container fade-in">
          <h2 className="text-2xl text-center font-semibold text-deep-blue mb-8">
            Wir benötigen jetzt nur noch Ihre E-Mail Adresse, um Ihnen das Ergebnis zusenden zu können.
          </h2>

          <h2 className="text-2xl text-center font-semibold text-deep-blue mb-8">
            Wie lautet Ihre E-Mail-Adresse?
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>


              <div className={getContainerClass()}>
                <div className="flex-shrink-0 pl-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  placeholder="E-Mail Adresse"
                  className="flex-grow py-3 px-3 bg-transparent border-0 focus:outline-none focus:ring-0"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  onBlur={handleBlur}
                />
              </div>

              {errors.email && touched && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div className="mt-8 flex justify-between">
              <CTAButton
                type="button"
                text="Zurück"
                back={true}
                onClick={handleBack}
                className="w-[48%]"
              />
              <CTAButton
                type="submit"
                text="Weiter"
                arrow={true}
                className={`w-[48%] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
            </div>
          </form>
        </div>

        <SecurityBadges />
      </main>

      <Footer />
    </div>
  );
};

export default Email;
